import { X, Check, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { trackCTAClick } from "@/lib/utils";

interface SlideInFormProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Exit-intent slide-in, in the site's editorial system (cream/ink/blue).
 * z-scale: header 50 · sticky CTA 60 · slide-in 70 · modal 80.
 */
const SlideInForm = ({ isOpen, onClose }: SlideInFormProps) => {
  const navigate = useNavigate();

  const handleCTAClick = () => {
    trackCTAClick({
      cta_text: "Get My Visibility Score",
      cta_location: "exit_intent_popup",
      cta_type: "digital_audit",
      cta_url: "/digital-audit",
    });
    onClose();
    navigate("/digital-audit/");
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed bottom-5 right-5 z-[70] w-[calc(100%-2.5rem)] max-w-sm pointer-events-auto animate-in slide-in-from-bottom-6 fade-in-0 zoom-in-95 duration-500 [animation-timing-function:cubic-bezier(0.16,1,0.3,1)] motion-reduce:animate-none"
      role="dialog"
      aria-modal="false"
      aria-labelledby="slidein-title"
    >
      <div className="relative overflow-hidden rounded-2xl border border-[#E9E6DE] bg-white shadow-[0_2px_4px_rgba(16,19,25,0.04),0_24px_56px_-16px_rgba(16,19,25,0.18),0_18px_44px_-18px_rgba(47,95,232,0.22)]">
        {/* solid brand hairline */}
        <div className="absolute top-0 inset-x-0 h-[2px] bg-[#2F5FE8]" />

        <button
          onClick={onClose}
          className="absolute right-3 top-3 z-10 p-1.5 rounded-full text-[#5A6270] hover:bg-[#F4F2EC] hover:text-[#101319] transition-colors"
          aria-label="Close popup"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="p-6 font-instrument">
          {/* eyebrow: matches the homepage hero language */}
          <div className="inline-flex items-center gap-2.5 font-mono-ibm text-[10px] tracking-[0.16em] uppercase text-[#2F5FE8]">
            <span className="w-5 h-[1px] bg-[#2F5FE8]" />
            Free visibility check · 2 minutes
          </div>

          <h3
            id="slidein-title"
            className="mt-3 text-[19px] leading-snug font-semibold tracking-[-0.02em] text-[#101319] pr-4"
          >
            Can customers actually find you?
          </h3>
          <p className="mt-1.5 text-[13.5px] leading-relaxed text-[#5A6270]">
            Run the free visibility score. See how you show up in search, and what it's costing you.
          </p>

          <ul className="mt-4 space-y-2">
            {[
              "Where customers can't find you",
              "Your site speed, SEO & listings",
              "The first fix worth making",
            ].map((item) => (
              <li key={item} className="flex items-center gap-2.5 text-[13px] text-[#101319]">
                <span className="grid h-[18px] w-[18px] shrink-0 place-items-center rounded-full bg-[#2F5FE8]/10">
                  <Check className="w-3 h-3 text-[#2F5FE8]" strokeWidth={3} />
                </span>
                {item}
              </li>
            ))}
          </ul>

          <button
            onClick={handleCTAClick}
            className="group mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-[#2F5FE8] px-6 py-3.5 text-[15px] font-bold text-white shadow-lg shadow-[#2F5FE8]/25 transition-all hover:bg-[#2049BE] hover:-translate-y-0.5 hover:shadow-xl hover:shadow-[#2F5FE8]/30 active:translate-y-0 motion-reduce:hover:translate-y-0"
          >
            Get My Visibility Score
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </button>

          <p className="mt-2.5 text-center text-[11px] font-medium text-[#8A8F99]">
            No signup. Built for Canadian service businesses.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SlideInForm;
